import { Alert, Box, Table, TableBody, TableHead, TableRow } from '@mui/material';

import ClientPaginatedTable from '../../../common/components/table/ClientPaginatedTable';
import {
  CustomButton,
  GeneralTableBox,
  HeadTableCell,
  NoDataSkeleton,
  RowTableCell,
  StatusChip,
  TableBodySkeleton,
} from '../../../common/components';
import { materialCell, materialMoney } from '../../../utils/materialDisplay';
import {
  INTERNAL_ORDER_MATERIALS_TEXTS as TEXTS,
  ORDER_ITEM_STATUS_COLORS,
  ORDER_ITEM_STATUS_LABELS,
} from '../constants/internalOrdersConstants';

const numberSx = { textAlign: 'right' };
const textSx = { textAlign: 'left' };
const BASE_COLUMN_COUNT = 8;
const MIN_TABLE_WIDTH = 960;
const COST_COLUMN_WIDTH = 200;

/**
 * Materiales de un pedido técnico, cargados al expandir la fila.
 * @param {Object} props
 * @param {Object} props.query Resultado de `useExternalOrder` del pedido expandido.
 * @returns {JSX.Element}
 */
const InternalOrderExpandedMaterials = ({ query }) => {
  const items = query.data?.items || [];
  const showCosts = items.some((item) => Object.hasOwn(item, 'materials_cost_ars'));
  const columnCount = BASE_COLUMN_COUNT + (showCosts ? 1 : 0);

  if (query.isError) {
    return (
      <Box sx={{ p: 1.5 }}>
        <Alert
          severity="error"
          action={(
            <CustomButton variant="outlined" size="small" onClick={() => query.refetch()}>
              {TEXTS.retry}
            </CustomButton>
          )}
        >
          {query.error?.response?.data?.message || TEXTS.loadError}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 1.25, md: 2 }, py: 1.5, backgroundColor: 'background.formTables' }}>
      <ClientPaginatedTable rows={items} resetKey={query.data?.uuid}>
        {(pageRows) => (
          <GeneralTableBox
            hasMaxHeight={false}
            fixedLayout
            minTableWidth={MIN_TABLE_WIDTH + (showCosts ? COST_COLUMN_WIDTH : 0)}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <HeadTableCell sx={{ ...textSx, width: 170 }}>{TEXTS.group}</HeadTableCell>
                  <HeadTableCell sx={{ ...textSx, width: 120 }}>{TEXTS.code}</HeadTableCell>
                  <HeadTableCell sx={textSx}>{TEXTS.material}</HeadTableCell>
                  <HeadTableCell sx={{ ...textSx, width: 110 }}>{TEXTS.unit}</HeadTableCell>
                  <HeadTableCell sx={{ ...numberSx, width: 110 }}>{TEXTS.requested}</HeadTableCell>
                  <HeadTableCell sx={{ ...numberSx, width: 120 }}>{TEXTS.consumed}</HeadTableCell>
                  <HeadTableCell sx={{ ...numberSx, width: 110 }}>{TEXTS.missing}</HeadTableCell>
                  <HeadTableCell sx={{ width: 140 }}>{TEXTS.status}</HeadTableCell>
                  {showCosts && (
                    <HeadTableCell sx={{ ...numberSx, width: COST_COLUMN_WIDTH }}>
                      {TEXTS.originalCost}
                    </HeadTableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {query.isLoading ? (
                  <TableBodySkeleton rows={3} />
                ) : items.length ? (
                  pageRows.map((item) => (
                    <TableRow key={item.uuid} hover>
                      <RowTableCell sx={textSx} title={item.group_name}>
                        {item.group_name || TEXTS.noGroup}
                      </RowTableCell>
                      <RowTableCell sx={textSx} title={item.article_code}>
                        {item.article_code}
                      </RowTableCell>
                      <RowTableCell sx={textSx} title={item.article_name}>
                        {item.article_name}
                      </RowTableCell>
                      <RowTableCell sx={textSx}>{item.unit_name}</RowTableCell>
                      <RowTableCell sx={numberSx}>
                        {materialCell('requested_quantity', item.requested_quantity)}
                      </RowTableCell>
                      <RowTableCell sx={numberSx}>
                        {materialCell('consumed_quantity', item.consumed_quantity)}
                      </RowTableCell>
                      <RowTableCell sx={numberSx}>
                        {materialCell('missing_quantity', item.missing_quantity)}
                      </RowTableCell>
                      <RowTableCell>
                        <StatusChip
                          size="small"
                          color={ORDER_ITEM_STATUS_COLORS[item.status] || 'default'}
                          label={ORDER_ITEM_STATUS_LABELS[item.status] || item.status}
                        />
                      </RowTableCell>
                      {showCosts && (
                        <RowTableCell sx={numberSx}>
                          {materialMoney(item.materials_cost_ars)}
                        </RowTableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <NoDataSkeleton colSpan={columnCount} message={TEXTS.empty} showSubtitle={false} />
                )}
              </TableBody>
            </Table>
          </GeneralTableBox>
        )}
      </ClientPaginatedTable>
    </Box>
  );
};

export default InternalOrderExpandedMaterials;
